import type { Chat } from '@trinity/chat';
import { KolSearchStatusEnum } from '@trinity/chat';
import type { ChatState, KolSearchState } from './interfaces';
import { generateEmptyKolSearch } from './state.initial-state';

function updateByIdentifier(state: KolSearchState.State, identifier: string, updater: (ks: Chat.KolSearch) => Chat.KolSearch): KolSearchState.State {
  return state.map(ks => ks.identifier === identifier ? updater(ks) : ks);
}

const kolStatusUpdateTypes: Set<string> = new Set([
  'kol-search-profile-selected',
  'kol-search-profile-available',
  'kol-search-form-submission-errored',
  'kol-search-profile-selection-errored',
]);

export function kolSearchesReducer(state: KolSearchState.State, action: ChatState.Action): KolSearchState.State {

  if (kolStatusUpdateTypes.has(action.type)) {
    return handleKolSearchStatusUpdate(state, action);
  }

  switch (action.type) {
    case 'kol-search-added':
      return state.concat(action.payload.item);

    case 'kol-search/recommendations-loading':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, recommendations: { ...ks.recommendations, status: 'loading' },
      }));

    case 'kol-search/recommendations-loaded':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, recommendations: { status: 'loaded', items: action.payload.items, total: action.payload.total },
      }));

    case 'kol-search/recommendations-added':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, recommendations: { ...ks.recommendations, items: ks.recommendations.items.concat(action.payload.items) },
      }));

    case 'kol-search-form-begin':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, status: KolSearchStatusEnum.FormEntry, form: { name: '' },
      }));

    case 'kol-search-form-updated':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, form: { ...ks.form, ...action.payload.form },
      }));

    case 'kol-search-submitted':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, status: KolSearchStatusEnum.FormSubmitted,
      }));

    case 'kol-search-results-available':
      return handleKolSearchResults(state, action.payload);

    case 'kol-search-cancelled':
      return handleKolSearchCancelled(state, action.payload.identifier);

    case 'kol-search/question-bank-question-selected':
      return [generateEmptyKolSearch()];

    case 'kol-search/new-search-initiated':
      return updateByIdentifier(state, action.payload.oldIdentifier, ks => ({
        ...ks, status: KolSearchStatusEnum.SearchCancelled,
      })).concat(generateEmptyKolSearch());

    default:
      return state;
  }

}

function handleKolSearchStatusUpdate(state: KolSearchState.State, action: ChatState.Action): KolSearchState.State {
  switch (action.type) {
    case 'kol-search-profile-selected':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, status: KolSearchStatusEnum.ProfileSelectionSubmitted,
      }));

    case 'kol-search-profile-available':
      return updateByIdentifier(state, action.payload.identifier, () => action.payload.result);

    case 'kol-search-form-submission-errored':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, status: KolSearchStatusEnum.FormSubmissionErrored, errorTraceId: action.payload.traceId,
      }));

    case 'kol-search-profile-selection-errored':
      return updateByIdentifier(state, action.payload.identifier, ks => ({
        ...ks, status: KolSearchStatusEnum.ProfileSelectionErrored, errorTraceId: action.payload.traceId,
      }));

    default:
      return state;
  }
}

function handleKolSearchResults(state: KolSearchState.State, payload: { identifier: string; result: Chat.KolSearch }): KolSearchState.State {
  const results = state.map(ks => ks.identifier === payload.identifier ? payload.result : ks);
  if (payload.result.status === KolSearchStatusEnum.NoResultsReturned) {
    const existingSearch = state.find(ks => ks.identifier === payload.identifier);
    if (existingSearch && existingSearch.results === null) {
      return results.concat(generateEmptyKolSearch());
    }
  }
  return results;
}

function handleKolSearchCancelled(state: KolSearchState.State, identifier: string): KolSearchState.State {
  return updateByIdentifier(state, identifier, ks => ({
    ...ks, status: KolSearchStatusEnum.SearchCancelled,
  })).concat(generateEmptyKolSearch());
}
